import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { ROUTES } from '../routes/routesConfig';
import { apiService } from '../services/api.services';
import { useCompanyStore } from '../store/companyStore';
import { clearCpfHashStorage } from '../utils/authHash';
import {
  buildEntraIdAuthorizeUrl,
  clearEntraIdTransaction,
  createEntraIdTransaction,
  hasEntraIdConfig,
  saveEntraIdTransaction,
  validateEntraIdCallback,
} from '../utils/entraIdAuthUtils';
import { redirectToEntraId } from '../utils/entraIdRedirect';
import { clearEntraIdIdentity, persistEntraIdIdentity } from '../utils/entraIdSession';
import { mapEntraLoginResponseToUser } from '../utils/entraIdUserMapper';

import { useAuth } from './useAuth';

export interface UseEntraIdLoginReturn {
  isLoading: boolean;
  error: string | null;
  signIn: () => Promise<void>;
}

const GENERIC_ENTRA_ERROR = 'Nenhum login Microsoft detectado.';

export const useEntraIdLogin = (): UseEntraIdLoginReturn => {
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { setAuthData, clearError: clearAuthError } = useAuth();
  const hasProcessedRef = useRef(false);

  const handleCallback = useCallback(async (code: string, stateFromUrl: string | null) => {
    setIsLoading(true);
    setError(null);

    try {
      const validated = validateEntraIdCallback(code, stateFromUrl);
      navigate(ROUTES.LOGIN_SSO, { replace: true });

      const response = await apiService.entraIdAuth.postEntraLogin({
        code: validated.code,
        codeVerifier: validated.codeVerifier,
        nonce: validated.nonce,
      });

      const entraUser = mapEntraLoginResponseToUser(response);
      clearCpfHashStorage();
      persistEntraIdIdentity(entraUser);
      clearEntraIdTransaction();

      useCompanyStore.getState().clearCompanySession();
      useCompanyStore.getState().setVinculos([]);
      useCompanyStore.getState().setSessionRole('BACKOFFICE');
      clearAuthError();
      setAuthData({ user: entraUser });

      navigate('/company', { state: { fromLogin: true }, replace: true });
    } catch (err) {
      clearEntraIdTransaction();
      clearEntraIdIdentity();
      navigate(ROUTES.LOGIN_SSO, { replace: true });
      const message = err instanceof Error && err.message
        ? err.message
        : GENERIC_ENTRA_ERROR;
      setError(message);
      setIsLoading(false);
    }
  }, [clearAuthError, navigate, setAuthData]);

  const signIn = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      if (!hasEntraIdConfig()) {
        throw new Error(GENERIC_ENTRA_ERROR);
      }

      const transaction = await createEntraIdTransaction();
      saveEntraIdTransaction(transaction);
      redirectToEntraId(await buildEntraIdAuthorizeUrl(transaction));
    } catch {
      clearEntraIdTransaction();
      setError(GENERIC_ENTRA_ERROR);
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (hasProcessedRef.current) {
      return;
    }
    hasProcessedRef.current = true;

    const entraError = searchParams.get('error');
    const code = searchParams.get('code');
    const state = searchParams.get('state');

    if (entraError) {
      clearEntraIdTransaction();
      navigate(ROUTES.LOGIN_SSO, { replace: true });
      setError(GENERIC_ENTRA_ERROR);
      setIsLoading(false);
      return;
    }

    if (code) {
      void handleCallback(code, state);
      return;
    }

    void signIn();
  }, [handleCallback, navigate, searchParams, signIn]);

  return {
    isLoading,
    error,
    signIn,
  };
};
